/* eslint-disable no-unused-vars */
/** @module Auth0PreLoginHandlerAccountLinking */
/* eslint-disable import/extensions */
import Auth0PreLoginHandlerBase from '../auth0-pre-login-handler-base.mst.mjs';
import Auth0UtilsBrowser from '../../../../utils/auth0-utils-browser.mjs';
import Auth0Constants from '../../../../utils/auth0-constants.mjs';
import logger from '../../../../utils/logging/auth0-logger.mjs';

const LINKING_TOKEN_KEY = Auth0Constants.AUTH0_LINKING_TOKEN_KEY;

/**
 * Pre-login handler for admin-initiated account linking flow.
 *
 * Flow:
 * 1. Admin generates linking token via SSAS endpoint
 * 2. User receives URL: https://app.com/loginauth?linkingToken=<uuid>
 * 3. This handler detects linkingToken in URL (pre-login, unauthenticated)
 * 4. Fetches IdP connection from SSAS anonymous endpoint
 * 5. Auto-redirects to SSO provider with token in appState
 * 6. After SSO authentication, post-login handler consumes token and links accounts
 */
class Auth0PreLoginHandlerAccountLinking extends Auth0PreLoginHandlerBase {
  /**
   * Check if this handler should run by detecting linkingToken in query string
   * @returns {Promise<boolean>} True if linkingToken present
   */
  async shouldHandle() {
    const linkingToken = this._qs.get(LINKING_TOKEN_KEY);
    if (linkingToken) {
      logger.info('Account linking token detected in URL', { token: linkingToken });
      return true;
    }
    return false;
  }

  /**
   * Run the account linking pre-login flow
   * @param {object} [viewOptions] Optional view initialization options
   * @returns {Promise<boolean>} True when handled
   */
  async run(viewOptions = {}) {
    try {
      const linkingToken = this._qs.get(LINKING_TOKEN_KEY);

      if (!linkingToken) {
        logger.error(`Account linking handler run without ${LINKING_TOKEN_KEY}`);
        throw new Error(`Missing ${LINKING_TOKEN_KEY} in URL`);
      }

      console.log('Starting account linking flow', { token: linkingToken });
      logger.info('Starting account linking flow', { token: linkingToken });

      // Fetch IdP connection using management API (will route through v3 Node proxy to SSAS)
      console.log('Fetching IdP connection for linking token via management API...');
      const responseData = await this._auth0ManagementApi.getLinkingTokenIdp({
        token: linkingToken,
      });

      console.log('IdP response data:', responseData);

      const { idpConnection } = responseData;

      if (!idpConnection) {
        console.error('IdP connection missing in response');
        logger.error('IdP connection missing in response', { token: linkingToken });
        throw new Error('Invalid linking token response');
      }

      console.log('IdP fetched successfully:', idpConnection);
      logger.info('IdP fetched successfully, initiating SSO redirect', {
        token: linkingToken,
        idpConnection,
      });

      // Get the IdP object from the socialIdpHelper
      const idp = this._socialIdpHelper.getIdpById(idpConnection);

      if (!idp) {
        console.error('IdP not found in supported IdPs:', idpConnection);
        logger.error('IdP not found in supported IdPs', { idpConnection });
        throw new Error(`Identity provider "${idpConnection}" is not configured`);
      }

      console.log('Found IdP:', idp.name, 'ID:', idp.id);

      // Use the IdP's signinWithRedirectHandler to initiate SSO flow
      // Pass linking token in additionalState so it survives the redirect
      // The IdP handler will:
      // 1. Set cookies for IdP context
      // 2. Redirect back to login page with additionalState in query params
      // 3. Login page will detect cookies and initiate OAuth flow with appState
      console.log('Calling IdP signinWithRedirectHandler with linking token in additionalState...');

      idp.signinWithRedirectHandler(
        this._appLoginUrl,
        { [LINKING_TOKEN_KEY]: linkingToken }, // additionalState
      );

      console.log('SSO redirect initiated via IdP handler');

      // Mark this request as handled
      return true;
    } catch (error) {
      console.error('Account linking pre-login handler failed:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);

      logger.error('Account linking pre-login handler failed', {
        error: error.message,
        stack: error.stack,
      });

      await this._handledUnexpectedException(error);

      // Cookie the error message so the login page can display it
      const errorMessage = `Account linking failed: ${error.message}`;
      Auth0UtilsBrowser.setAuth0IsError(errorMessage);

      // Redirect to login page without token
      console.log('Redirecting to login page after error:', this._appLoginUrl);
      window.location.href = this._appLoginUrl;

      return true;
    }
  }
}

export default Auth0PreLoginHandlerAccountLinking;
